<script setup lang="ts">
import { computed, type Ref } from 'vue';
import { useLayout } from '@/composables/useLayout';
import type Carousel from '@/components/widgets/Carousel.vue';

// Tipo antigo (Swiper.vue)
type SwiperControls = {
  scrollNext: () => void;
  scrollPrev: () => void;
  currentScrollLeft: Ref<number>;
  currentScrollWidth: Ref<number>;
  currentClientWidth: Ref<number>;
};

// Suporte para ambos os tipos
type SwiperRefType = InstanceType<typeof Carousel> | SwiperControls | null;

const props = withDefaults(
  defineProps<{
    iconPath?: string;
    title?: string;
    seeMoreUrl?: string;
    showSeeMore?: boolean;
    providertitle?: {
      background?: string;
      borderRadius?: string;
      marginBottom?: string;
      paddingBottom?: any;
      paddingRight?: string;
    };
    showNavigation?: boolean;
    swiperRef?: SwiperRefType;
    scrollDistance?: number;
    prevIcon?: string;
    nextIcon?: string;
    /**
     * 'bordered' = "VER MAIS" com borda ao redor (padrão para seções sem setas)
     * 'arrow-bordered' = "VER MAIS" sem borda + seta com borda (para seções numeric)
     */
    seeMoreVariant?: 'bordered' | 'arrow-bordered';
  }>(),
  {
    showSeeMore: true,
    showNavigation: false,
    seeMoreVariant: 'bordered',
    prevIcon: 'perfil-oficial/back',
    nextIcon: 'perfil-oficial/next',
  },
);

const { isMobile } = useLayout();

const isSeeMoreButtonVisible = computed<boolean>(() => {
  return props.showSeeMore && !!props.seeMoreUrl;
});

// Verifica se é o tipo novo (Carousel)
const isCarouselType = (
  ref: SwiperRefType,
): ref is InstanceType<typeof Carousel> => {
  return ref !== null && 'scrollToLeft' in ref && 'scrollToRight' in ref;
};

// Verifica se é o tipo antigo (SwiperControls)
const isSwiperControlsType = (ref: SwiperRefType): ref is SwiperControls => {
  return (
    ref !== null &&
    'scrollNext' in ref &&
    'scrollPrev' in ref &&
    'currentScrollLeft' in ref
  );
};

const isLeftButtonDisabled = computed(() => {
  if (!props.swiperRef) return true;

  // Tipo novo (Carousel)
  if (isCarouselType(props.swiperRef)) {
    return props.swiperRef.isNavigationLeftButtonDisabled ?? true;
  }

  // Tipo antigo (SwiperControls)
  if (isSwiperControlsType(props.swiperRef)) {
    const scrollLeft = props.swiperRef.currentScrollLeft?.value ?? 0;

    return scrollLeft <= 0;
  }

  return true;
});

const isRightButtonDisabled = computed(() => {
  if (!props.swiperRef) return true;

  // Tipo novo (Carousel)
  if (isCarouselType(props.swiperRef)) {
    return props.swiperRef.isNavigationRightButtonDisabled ?? true;
  }

  // Tipo antigo (SwiperControls)
  if (isSwiperControlsType(props.swiperRef)) {
    const { currentScrollLeft, currentClientWidth, currentScrollWidth } =
      props.swiperRef;

    const scrollLeft = currentScrollLeft?.value ?? 0;
    const clientWidth = currentClientWidth?.value ?? 0;
    const scrollWidth = currentScrollWidth?.value ?? 0;

    // Se não há overflow, desabilita o botão direito
    if (scrollWidth <= clientWidth) return true;

    return scrollLeft + clientWidth >= scrollWidth;
  }

  return true;
});

const handleScrollLeft = () => {
  if (!props.swiperRef) return;

  if (isCarouselType(props.swiperRef)) {
    props.swiperRef.scrollToLeft(props.scrollDistance ?? 300);
  } else if (isSwiperControlsType(props.swiperRef)) {
    props.swiperRef.scrollPrev();
  }
};

const handleScrollRight = () => {
  if (!props.swiperRef) return;

  if (isCarouselType(props.swiperRef)) {
    props.swiperRef.scrollToRight(props.scrollDistance ?? 300);
  } else if (isSwiperControlsType(props.swiperRef)) {
    props.swiperRef.scrollNext();
  }
};
</script>

<template>
  <div
    :class="[
      $style.header,
      { [$style.headerWithBackground]: isSeeMoreButtonVisible },
    ]"
    :draggable="false"
  >
    <div :class="$style.titleContainer">
      <div
        class="w-[36px] h-[36px] lg:w-[45px] lg:h-[45px] flex-shrink-0 flex items-center justify-center"
      >
        <NuxtIcon
          v-if="props.iconPath"
          class="text-[36px] lg:text-[45px] w-full h-full"
          :filled="true"
          :name="props.iconPath"
        />
      </div>
      <p :class="$style.titleText">{{ props.title }}</p>
    </div>

    <!-- VER MAIS: variante 'arrow-bordered' (numeric) - texto sem borda + seta com borda -->
    <NuxtLink
      v-if="isSeeMoreButtonVisible && props.seeMoreVariant === 'arrow-bordered'"
      class="flex items-center justify-center ml-auto gap-[5px]"
      :draggable="false"
      no-prefetch
      :to="props.seeMoreUrl!"
    >
      <span class="text-[11px] md:px-2.5 text-white font-bold leading-4"
        >VER MAIS</span
      >
      <span :class="$style.seeMoreArrowBordered">
        <NuxtIcon
          class="text-[10px] text-white"
          :filled="true"
          name="arrows/chevron_right"
        />
      </span>
    </NuxtLink>

    <!-- VER MAIS: variante 'bordered' (padrão) - texto com borda ao redor -->
    <NuxtLink
      v-else-if="isSeeMoreButtonVisible"
      :class="[
        'flex items-center justify-center ml-auto gap-1 px-2.5',
        !props.showNavigation ? $style.seeMoreBordered : '',
      ]"
      :draggable="false"
      no-prefetch
      :to="props.seeMoreUrl!"
    >
      <span class="text-[11px] md:px-2.5 text-white font-bold leading-4"
        >VER MAIS</span
      >
    </NuxtLink>

    <div
      v-if="showNavigation && !isMobile"
      :class="$style.actions"
    >
      <slot name="actions" />

      <div class="flex items-center justify-center">
        <button
          class="flex items-center justify-center rounded-full text-white disabled:opacity-50 px-2.5"
          :disabled="isLeftButtonDisabled"
          @click="handleScrollLeft"
        >
          <NuxtIcon
            class="text-[15px]"
            :name="props.prevIcon"
        /></button>
        <button
          class="flex items-center justify-center rounded-full bg-gray-700 text-white disabled:opacity-50 px-2.5"
          :disabled="isRightButtonDisabled"
          @click="handleScrollRight"
        >
          <NuxtIcon
            class="text-[15px]"
            :name="props.nextIcon"
          />
        </button>
      </div>
    </div>

    <div
      v-if="showNavigation && isMobile"
      :class="$style.mobileActions"
    >
      <slot name="actions" />

      <div
        class="rounded-[12px]"
        style="background: rgba(63, 63, 70, 0.4)"
      >
        <button
          class="flex items-center justify-center rounded-full text-white disabled:opacity-50 w-8 h-8"
          :disabled="isLeftButtonDisabled"
          @click="handleScrollLeft"
        >
          <NuxtIcon
            class="text-[16px]"
            :name="props.prevIcon"
          />
        </button>
      </div>

      <div
        class="rounded-[12px]"
        style="background: rgba(63, 63, 70, 0.4)"
      >
        <button
          class="flex items-center justify-center rounded-full text-white disabled:opacity-50 w-8 h-8"
          :disabled="isRightButtonDisabled"
          @click="handleScrollRight"
        >
          <NuxtIcon
            class="text-[16px]"
            :name="props.nextIcon"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style module lang="postcss">
.header {
  @apply flex justify-between items-center rounded-md mb-2.5 lg:mb-4 pr-[10px];
  @apply md:[background:linear-gradient(270deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.00)_100%)];
  user-select: none;
}

.headerWithBackground {
  @apply [background:linear-gradient(270deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.00)_100%)];
}

.titleContainer {
  @apply flex items-center h-[31px] lg:h-[45px] justify-center gap-2.5 md:gap-2;
}

.titleText {
  @apply text-white text-[16px] lg:text-[20px] font-bold leading-4;
}

.actions {
  @apply flex items-center gap-3;
}

.mobileActions {
  @apply flex items-center gap-2;
}

.seeMoreBordered {
  border-radius: 5.11px;
  border: 1.022px solid rgba(255, 255, 255, 0.1);
  padding: 2.044px 7.154px;
  align-items: flex-start;
}

.seeMoreArrowBordered {
  @apply flex items-center justify-center;
  border-radius: 5.11px;
  border: 1.022px solid rgba(255, 255, 255, 0.1);
  padding: 4px 7px;
}
</style>
